import { useEffect, useState } from 'react';
import type { MatchView } from './useMatch.ts';

function secondsUntil(iso: string | null): number | null {
  if (!iso) return null;
  const remaining = Math.ceil((new Date(iso).getTime() - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

/**
 * The other player's name.
 *
 * Finds them by looking for a seat that is not yours rather than by flipping
 * 1 and 2, so this still reads correctly on a spec that numbers its seats
 * differently. With more than two seats it names one of them, which is as good
 * as "your opponent" can be until the copy itself understands three players.
 */
function opponentName(view: MatchView): string {
  if (!view.seats || view.yourSeat === null) return 'Your opponent';
  for (const seat of view.spec.rules.seats) {
    if (seat === view.yourSeat) continue;
    const player = view.seats[seat];
    if (player) return player.displayName;
  }
  return 'Your opponent';
}

/**
 * The one place match state is put into words.
 *
 * It is a single `aria-live="polite"` region that is also the visible status, so
 * what a screen reader hears and what a sighted player reads can never disagree.
 * Announcements are sentences, not fragments, because they are read out of
 * context.
 */
export function statusMessage(view: MatchView): string {
  if (view.connection === 'reconnecting') return 'Reconnecting to the arena.';

  if (view.loadingRules) return 'Loading the rules for this game.';

  switch (view.phase) {
    case 'connecting':
      return 'Connecting to the arena.';
    case 'idle':
      return 'Ready to play.';
    case 'resuming':
      return 'You have a match in progress. Loading the board.';
    case 'queued':
      return view.queuePosition === 1
        ? 'Waiting for an opponent.'
        : `Waiting for an opponent, position ${view.queuePosition ?? 1} in the queue.`;
    case 'hosting': {
      if (!view.lobby) return 'Your game is open, waiting for someone to join.';
      // The code is spelled out letter by letter: a screen reader would
      // otherwise try to pronounce it as a word.
      const spelled = view.lobby.code.split('').join(' ');
      const queued = view.lobby.waiting.length;
      if (queued === 0) {
        return `Your game is open. Share the code ${spelled}, or the QR code on screen.`;
      }
      return `Your game is open, with ${queued} ${queued === 1 ? 'player' : 'players'} waiting. Share the code ${spelled}, or the QR code on screen.`;
    }
    case 'waiting':
      return view.waitingIn
        ? `You are in ${view.waitingIn.hostName}'s queue, number ${view.waitingIn.position} in line.`
        : 'You are waiting for a game.';
    case 'playing': {
      if (view.connection === 'opponent_away') {
        const seconds = secondsUntil(view.graceEndsAt);
        return seconds === null
          ? `${opponentName(view)} disconnected.`
          : `${opponentName(view)} disconnected. They forfeit in ${seconds} seconds.`;
      }
      if (view.pendingColumn !== null) return 'Sending your move.';
      // The turn prompts are the spec's words, so an authored game can call a
      // move whatever its rules actually call it.
      const { statuses } = view.spec.presentation.labels;
      if (view.yourSeat !== null && view.toMove === view.yourSeat) return `${statuses.yourTurn}.`;
      return `${statuses.theirTurn}: ${opponentName(view)}.`;
    }
    case 'ended': {
      if (!view.result) return 'The match is over.';
      const youWon =
        view.yourSeat !== null &&
        view.result.result === (view.yourSeat === 1 ? 'seat1_win' : 'seat2_win');
      const drawn = view.result.result === 'draw';

      const headline = drawn ? 'Drawn game.' : youWon ? 'You win.' : `${opponentName(view)} wins.`;
      switch (view.result.reason) {
        case 'four_in_a_row':
          return `${headline} Four in a row.`;
        case 'board_full':
          return `${headline} The board is full.`;
        case 'resign':
          return `${headline} By resignation.`;
        case 'abandon':
          return `${headline} The other player did not come back.`;
        case 'timeout':
          return `${headline} On time.`;
      }
    }
  }
}

export interface StatusBarProps {
  readonly view: MatchView;
}

export function StatusBar({ view }: StatusBarProps) {
  const [, forceTick] = useState(0);

  // Only ticks while a forfeit countdown is actually running.
  useEffect(() => {
    if (view.connection !== 'opponent_away' || !view.graceEndsAt) return;
    const timer = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [view.connection, view.graceEndsAt]);

  const yourTurn =
    view.phase === 'playing' &&
    view.yourSeat !== null &&
    view.toMove === view.yourSeat &&
    view.connection === 'live';

  return (
    <div className="status">
      <p
        className={`status__line${yourTurn ? ' status__line--active' : ''}`}
        role="status"
        aria-live="polite"
      >
        {view.yourSeat !== null && view.phase !== 'ended' ? (
          <span
            className={`status__seat status__seat--${view.toMove}`}
            aria-hidden="true"
            data-testid="turn-indicator"
          />
        ) : null}
        {statusMessage(view)}
      </p>

      {view.notice ? (
        <p className="status__notice" role="alert">
          {view.notice}
        </p>
      ) : null}
    </div>
  );
}
